import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PullToRefresh } from '@/components/PullToRefresh';
import { Kanban, Plus, Users, Trash2, ClipboardList, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TASK_STATUSES = [
  { value: 'todo', label: 'To Do', color: 'bg-slate-500/10 text-slate-400 border-slate-500/20' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  { value: 'done', label: 'Done', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'text-slate-400' },
  { value: 'medium', label: 'Medium', color: 'text-amber-400' },
  { value: 'high', label: 'High', color: 'text-red-400' },
];

export default function ProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [projForm, setProjForm] = useState({ title: '', description: '' });
  const [taskForm, setTaskForm] = useState({ title: '', description: '', priority: 'medium', status: 'todo' });

  const fetchProjects = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects`, { credentials: 'include' });
      if (res.ok) setProjects(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchTasks = async (projectId) => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/tasks`, { credentials: 'include' });
      if (res.ok) setTasks(await res.json());
    } catch (err) { console.error(err); }
  };

  const handleRefresh = useCallback(async () => {
    await fetchProjects();
    if (selectedProject) {
      await fetchTasks(selectedProject.project_id);
    }
    toast.success('Projects refreshed');
  }, [fetchProjects, selectedProject]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const handleCreateProject = async () => {
    try {
      const res = await fetch(`${API}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(projForm)
      });
      if (res.ok) {
        toast.success('Project created');
        setCreateOpen(false);
        setProjForm({ title: '', description: '' });
        fetchProjects();
      }
    } catch { toast.error('Failed to create project'); }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await fetch(`${API}/projects/${projectId}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Project deleted');
      if (selectedProject?.project_id === projectId) setSelectedProject(null);
      fetchProjects();
    } catch { toast.error('Failed to delete'); }
  };

  const handleJoinProject = async (projectId) => {
    try {
      await fetch(`${API}/projects/${projectId}/join`, { method: 'POST', credentials: 'include' });
      toast.success('Joined project');
      fetchProjects();
    } catch { toast.error('Failed to join'); }
  };

  const handleCreateTask = async () => {
    if (!selectedProject) return;
    try {
      const res = await fetch(`${API}/projects/${selectedProject.project_id}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(taskForm)
      });
      if (res.ok) {
        toast.success('Task created');
        setTaskOpen(false);
        setTaskForm({ title: '', description: '', priority: 'medium', status: 'todo' });
        fetchTasks(selectedProject.project_id);
      }
    } catch { toast.error('Failed to create task'); }
  };

  const handleUpdateTaskStatus = async (taskId, status) => {
    try {
      await fetch(`${API}/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });
      fetchTasks(selectedProject.project_id);
    } catch { toast.error('Failed to update task'); }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await fetch(`${API}/tasks/${taskId}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Task deleted');
      fetchTasks(selectedProject.project_id);
    } catch { toast.error('Failed to delete task'); }
  };

  const openProjectDetail = (proj) => {
    setSelectedProject(proj);
    fetchTasks(proj.project_id);
  };

  return (
    <PullToRefresh onRefresh={handleRefresh} className="min-h-screen">
      <div data-testid="projects-page" className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Projects Portal
            </h1>
            <p className="text-slate-500 text-sm mt-1">Collaborative projects and task management</p>
          </div>
          <Button
            data-testid="create-project-btn"
            onClick={() => setCreateOpen(true)}
            className="bg-[#003087] hover:bg-[#003087]/90 text-white w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" /> New Project
          </Button>
        </div>

        {selectedProject ? (
        /* Project Detail View */
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="ghost" onClick={() => setSelectedProject(null)} className="text-slate-400 hover:text-white text-sm px-2">
              Projects
            </Button>
            <ArrowRight className="h-3 w-3 text-slate-600" />
            <span className="text-sm text-slate-200 truncate max-w-[200px]">{selectedProject.title}</span>
          </div>

          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="border-b border-slate-800 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-base sm:text-lg text-slate-200 truncate">{selectedProject.title}</CardTitle>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{selectedProject.description}</p>
              </div>
              <Button
                data-testid="create-task-btn"
                onClick={() => setTaskOpen(true)}
                className="bg-[#003087] hover:bg-[#003087]/90 text-white w-full sm:w-auto"
                size="sm"
              >
                <Plus className="h-3 w-3 mr-1" /> Add Task
              </Button>
            </CardHeader>
            <CardContent className="p-3 sm:p-4">
              {/* Task Board Columns - Horizontal scroll on mobile */}
              <div className="flex md:grid md:grid-cols-3 gap-4 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 snap-x snap-mandatory md:snap-none">
                {TASK_STATUSES.map(col => {
                  const colTasks = tasks.filter(t => t.status === col.value);
                  return (
                    <div key={col.value} className="min-w-[280px] md:min-w-0 flex-shrink-0 snap-start space-y-3">
                      <div className="flex items-center gap-2 mb-2 sticky top-0 bg-slate-900 py-1">
                        <Badge className={`text-xs ${col.color}`}>{col.label}</Badge>
                        <span className="text-xs text-slate-600">{colTasks.length}</span>
                      </div>
                      {colTasks.map(task => {
                        const pri = PRIORITIES.find(p => p.value === task.priority);
                        return (
                          <Card key={task.task_id} data-testid={`task-${task.task_id}`} className="bg-slate-800/50 border-slate-700/50 shadow-sm">
                            <CardContent className="p-3">
                              <div className="flex items-start justify-between gap-2">
                                <h4 className="text-sm text-slate-200 font-medium line-clamp-2">{task.title}</h4>
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => handleDeleteTask(task.task_id)}
                                  className="h-6 w-6 p-0 text-slate-500 hover:text-red-400 flex-shrink-0"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                              {task.description && (
                                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{task.description}</p>
                              )}
                              <div className="flex items-center justify-between mt-2">
                                <span className={`text-[10px] font-mono ${pri?.color || ''}`}>{task.priority?.toUpperCase()}</span>
                                <Select
                                  value={task.status}
                                  onValueChange={(v) => handleUpdateTaskStatus(task.task_id, v)}
                                >
                                  <SelectTrigger className="h-6 w-auto text-[10px] bg-transparent border-slate-700 text-slate-400 px-2">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#0f172a] border-slate-700">
                                    {TASK_STATUSES.map(s => (
                                      <SelectItem key={s.value} value={s.value} className="text-slate-300 text-xs focus:bg-slate-800">{s.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                      {colTasks.length === 0 && (
                        <p className="text-xs text-slate-600 text-center py-4">No tasks</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Projects List */
        <>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-[140px] sm:h-[160px] bg-slate-900 border border-slate-800 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <Kanban className="h-10 w-10 sm:h-12 sm:w-12 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-500">No projects yet</p>
              <p className="text-xs text-slate-600 mt-1">Create a project to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {projects.map((proj) => {
                const isMember = proj.members?.includes(user?.user_id);
                return (
                  <Card
                    key={proj.project_id}
                    data-testid={`project-${proj.project_id}`}
                    className="bg-slate-900 border-slate-800 shadow-sm hover:border-slate-700 transition-colors cursor-pointer active:scale-[0.98]"
                    onClick={() => openProjectDetail(proj)}
                  >
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-medium text-slate-200 line-clamp-1">{proj.title}</h3>
                        <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px] flex-shrink-0">
                          {proj.status?.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-2 line-clamp-2">{proj.description}</p>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-800/50">
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <Users className="h-3 w-3" />
                          <span>{proj.members?.length || 0}</span>
                        </div>
                        <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                          {!isMember && (
                            <Button
                              data-testid={`join-project-${proj.project_id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => handleJoinProject(proj.project_id)}
                              className="h-7 text-xs border-slate-700 text-slate-300 hover:bg-slate-800"
                            >
                              Join
                            </Button>
                          )}
                          <Button
                            data-testid={`delete-project-${proj.project_id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteProject(proj.project_id)}
                            className="h-7 text-xs text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Create Project Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#0f172a] border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-200">New Project</DialogTitle>
            <DialogDescription className="text-slate-500">Create a collaborative project</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input data-testid="project-title-input" placeholder="Project title" value={projForm.title} onChange={(e) => setProjForm({...projForm, title: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-200" />
            <Textarea data-testid="project-desc-input" placeholder="Description" value={projForm.description} onChange={(e) => setProjForm({...projForm, description: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-200" />
          </div>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setCreateOpen(false)} className="text-slate-400">Cancel</Button>
            <Button data-testid="project-save-btn" onClick={handleCreateProject} className="bg-[#003087] hover:bg-[#003087]/90 text-white">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
        <DialogContent className="bg-[#0f172a] border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-200">New Task</DialogTitle>
            <DialogDescription className="text-slate-500">Add a task to {selectedProject?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input data-testid="task-title-input" placeholder="Task title" value={taskForm.title} onChange={(e) => setTaskForm({...taskForm, title: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-200" />
            <Textarea data-testid="task-desc-input" placeholder="Description" value={taskForm.description} onChange={(e) => setTaskForm({...taskForm, description: e.target.value})} className="bg-slate-900 border-slate-800 text-slate-200" />
            <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({...taskForm, priority: v})}>
              <SelectTrigger data-testid="task-priority-select" className="bg-slate-900 border-slate-800 text-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0f172a] border-slate-700">
                {PRIORITIES.map(p => (
                  <SelectItem key={p.value} value={p.value} className="text-slate-300 focus:bg-slate-800">{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setTaskOpen(false)} className="text-slate-400">Cancel</Button>
            <Button data-testid="task-save-btn" onClick={handleCreateTask} className="bg-[#003087] hover:bg-[#003087]/90 text-white">Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </PullToRefresh>
  );
}
